/**
 * useDocumentPage.tsx
 *
 * Shared stateful hook that powers both the Dashboard and ExpiryItems pages.
 * Encapsulates:
 *  - data fetching (items, loading)
 *  - modal state (open/close, which item, which mode)
 *  - row selection
 *  - search query
 *  - all per-row CRUD handlers
 *  - all bulk-action handlers
 *
 * Pages that need page-specific extras (filter cards, URL params, extra modals)
 * simply add those on top of what this hook provides.
 *
 * NOTE: This is .tsx (not .ts) because the toast callbacks render JSX nodes.
 */

import React, { useState, useCallback } from "react";
import toast from "react-hot-toast";
import {
  useExpiryItems,
  useCreateExpiryItem,
  useUpdateExpiryItem,
  useDeleteExpiryItem,
  useArchiveItem,
  useSendReminderEmail,
  useImportCsv,
  ExpiryItem,
} from "./useExpiryItems";
import { formatLocalDate, buildCsv } from "../utils/expiryUtils";
import { TOAST, CSV_FILENAME } from "../constants/expiry";

// ─── Public interface ─────────────────────────────────────────────────────────

export interface DocumentPageState {
  // ── Data ──────────────────────────────────────────────────────────────────
  items: ExpiryItem[];
  isLoading: boolean;

  // ── Modal ──────────────────────────────────────────────────────────────────
  isModalOpen: boolean;
  editingItem: ExpiryItem | null;
  modalRenewalMode: boolean;
  modalFocusExpiry: boolean;
  closeModal: () => void;

  // ── History Drawer ─────────────────────────────────────────────────────────
  drawerItem: ExpiryItem | null;
  openDrawer: (item: ExpiryItem) => void;
  closeDrawer: () => void;

  // ── Row selection ──────────────────────────────────────────────────────────
  selectedRows: Set<string>;
  handleRowSelect: (row: ExpiryItem, selected: boolean) => void;
  handleClearSelection: () => void;

  // ── Search ─────────────────────────────────────────────────────────────────
  searchQuery: string;
  setSearchQuery: (q: string) => void;

  // ── Per-row handlers ───────────────────────────────────────────────────────
  handleCreate: () => void;
  handleEdit: (item: ExpiryItem) => void;
  handleAddFields: (item: ExpiryItem) => void;
  handleRenewItem: (item: ExpiryItem) => void;
  handleSendReminder: (item: ExpiryItem) => void;
  handleDelete: (id: string) => Promise<void>;
  handleSubmit: (itemData: Record<string, unknown>) => Promise<void>;
  handleRowAction: (row: ExpiryItem, action: string) => void;

  // ── Bulk handlers ──────────────────────────────────────────────────────────
  handleBulkRenew: (newDate: string) => Promise<void>;
  handleBulkEditExpiry: (newDate: string) => Promise<void>;
  handleBulkAssignOwner: (ownerEmails: string[]) => Promise<void>;
  handleBulkChangeCategory: (category: string) => Promise<void>;
  handleBulkSnooze: (days: number) => void;
  handleBulkArchive: () => void;
  handleBulkExport: () => void;
  /** Accepts raw CSV text and sends it to POST /expiry/import-csv in one request. */
  handleImport: (csvText: string) => Promise<void>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useDocumentPage(): DocumentPageState {
  const { data: items = [], isLoading } = useExpiryItems();
  const createMutation = useCreateExpiryItem();
  const updateMutation = useUpdateExpiryItem();
  const deleteMutation = useDeleteExpiryItem();
  const archiveMutation = useArchiveItem();
  const sendReminderMutation = useSendReminderEmail();
  const importCsvMutation = useImportCsv();

  // ── Modal state ────────────────────────────────────────────────────────────
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ExpiryItem | null>(null);
  const [modalRenewalMode, setModalRenewalMode] = useState(false);
  const [modalFocusExpiry, setModalFocusExpiry] = useState(false);

  // ── Drawer state ───────────────────────────────────────────────────────────
  const [drawerItem, setDrawerItem] = useState<ExpiryItem | null>(null);
  const openDrawer = useCallback((item: ExpiryItem) => setDrawerItem(item), []);
  const closeDrawer = useCallback(() => setDrawerItem(null), []);

  // ── Selection state ────────────────────────────────────────────────────────
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  // ── Search state ───────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");

  // ─── Internal helper ───────────────────────────────────────────────────────
  const openModal = useCallback(
    (item: ExpiryItem | null, renewal: boolean, focusExpiry: boolean) => {
      setEditingItem(item);
      setModalRenewalMode(renewal);
      setModalFocusExpiry(focusExpiry);
      setIsModalOpen(true);
    },
    []
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingItem(null);
    setModalRenewalMode(false);
    setModalFocusExpiry(false);
  }, []);

  // ── CRUD handlers ──────────────────────────────────────────────────────────

  const handleCreate = useCallback(
    () => openModal(null, false, false),
    [openModal]
  );

  const handleEdit = useCallback(
    (item: ExpiryItem) => openModal(item, false, false),
    [openModal]
  );

  const handleAddFields = useCallback(
    (item: ExpiryItem) => openModal(item, false, true),
    [openModal]
  );

  const handleRenewItem = useCallback(
    (item: ExpiryItem) => openModal(item, true, false),
    [openModal]
  );

  const handleSendReminder = useCallback(
    (item: ExpiryItem) => {
      // Always call the API — let the backend validate owners.
      // If no owners are assigned the backend returns a clear 400 error
      // which is shown via the toast.promise error handler below.
      toast.promise(
        sendReminderMutation.mutateAsync(item.id),
        {
          loading: `Sending reminder for "${item.name}"…`,
          success: (result) =>
            `📧 Reminder sent to ${result.recipients.join(", ")}`,
          error: (err: any) => {
            const msg: string =
              err?.response?.data?.error ?? "Failed to send reminder email";
            // If the error is "no owners", append a helpful hint
            if (msg.toLowerCase().includes("owner")) {
              return `${msg} — open the document to add one`;
            }
            return msg;
          },
        },
        { duration: TOAST.normal }
      );
    },
    [sendReminderMutation]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (!window.confirm("Are you sure you want to delete this item?")) return;
      try {
        await deleteMutation.mutateAsync(id);
        toast.success("Item deleted successfully");
        setSelectedRows((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { error?: string } } })
          ?.response?.data?.error;
        toast.error(msg ?? "Failed to delete item");
      }
    },
    [deleteMutation]
  );

  const handleSubmit = useCallback(
    async (itemData: Record<string, unknown>) => {
      try {
        if (editingItem) {
          await updateMutation.mutateAsync({ id: editingItem.id, ...itemData });
          toast.success("Item updated successfully");
        } else {
          await createMutation.mutateAsync(
            itemData as Omit<ExpiryItem, "id" | "created_at" | "updated_at">
          );
          toast.success("Item created successfully");
        }
        closeModal();
      } catch (err: unknown) {
        const msg = (err as { response?: { data?: { error?: string } } })
          ?.response?.data?.error;
        toast.error(msg ?? "Failed to save item");
      }
    },
    [editingItem, updateMutation, createMutation, closeModal]
  );

  const handleRowAction = useCallback(
    (row: ExpiryItem, action: string) => {
      if (action === "edit") {
        handleEdit(row);
      } else if (action === "archive") {
        toast.promise(archiveMutation.mutateAsync(row.id), {
          loading: `Archiving "${row.name}"…`,
          success: `"${row.name}" archived. Find it under Archive in the menu.`,
          error: "Failed to archive item",
        });
      }
    },
    [handleEdit, archiveMutation]
  );

  // ── Row selection ──────────────────────────────────────────────────────────

  const handleRowSelect = useCallback((row: ExpiryItem, selected: boolean) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      selected ? next.add(row.id) : next.delete(row.id);
      return next;
    });
  }, []);

  const handleClearSelection = useCallback(
    () => setSelectedRows(new Set()),
    []
  );

  // ── Bulk handlers ──────────────────────────────────────────────────────────

  const handleBulkRenew = useCallback(
    async (newDate: string) => {
      const selected = items.filter((i) => selectedRows.has(i.id));
      await Promise.all(
        selected.map((item) =>
          updateMutation.mutateAsync({
            id: item.id,
            expiry_date: newDate,
            isRenewal: true,
          })
        )
      );
      toast.success(
        `${selected.length} document${selected.length !== 1 ? "s" : ""} renewed to ${formatLocalDate(newDate)}`
      );
      setSelectedRows(new Set());
    },
    [items, selectedRows, updateMutation]
  );

  const handleBulkEditExpiry = useCallback(
    async (newDate: string) => {
      const selected = items.filter((i) => selectedRows.has(i.id));
      await Promise.all(
        selected.map((item) =>
          updateMutation.mutateAsync({
            id: item.id,
            expiry_date: newDate,
            isExpiryEdit: true,
          })
        )
      );
      toast.success(
        `Expiry date updated to ${formatLocalDate(newDate)} for ${selected.length} document${selected.length !== 1 ? "s" : ""}`
      );
      setSelectedRows(new Set());
    },
    [items, selectedRows, updateMutation]
  );

  const handleBulkAssignOwner = useCallback(
    async (ownerEmails: string[]) => {
      const selected = items.filter((i) => selectedRows.has(i.id));
      await Promise.all(
        selected.map((item) =>
          updateMutation.mutateAsync({ id: item.id, owner_emails: ownerEmails })
        )
      );
      toast.success(
        `Owner${ownerEmails.length > 1 ? "s" : ""} assigned to ${selected.length} document${selected.length !== 1 ? "s" : ""}`
      );
      setSelectedRows(new Set());
    },
    [items, selectedRows, updateMutation]
  );

  const handleBulkChangeCategory = useCallback(
    async (category: string) => {
      const selected = items.filter((i) => selectedRows.has(i.id));
      await Promise.all(
        selected.map((item) =>
          updateMutation.mutateAsync({ id: item.id, category })
        )
      );
      toast.success(
        `Category updated to "${category}" for ${selected.length} document${selected.length !== 1 ? "s" : ""}`
      );
      setSelectedRows(new Set());
    },
    [items, selectedRows, updateMutation]
  );

  const handleBulkSnooze = useCallback(
    async (days: number) => {
      const selected = items.filter((i) => selectedRows.has(i.id));
      if (selected.length === 0) return;

      // Compute snooze-until date: today + days (YYYY-MM-DD)
      const snoozeUntil = new Date();
      snoozeUntil.setDate(snoozeUntil.getDate() + days);
      const snoozed_until = snoozeUntil.toISOString().split("T")[0];

      try {
        await Promise.all(
          selected.map((item) =>
            updateMutation.mutateAsync({ id: item.id, snoozed_until })
          )
        );
        toast.success(
          `Reminders snoozed until ${new Date(snoozed_until + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} for ${selected.length} document${selected.length !== 1 ? "s" : ""}`,
          { duration: 5000 }
        );
        setSelectedRows(new Set());
      } catch {
        toast.error("Failed to snooze some documents. Please try again.");
      }
    },
    [items, selectedRows, updateMutation]
  );

  const handleBulkArchive = useCallback(async () => {
    const selected = items.filter((i) => selectedRows.has(i.id));
    if (selected.length === 0) return;
    try {
      await Promise.all(
        selected.map((item) => archiveMutation.mutateAsync(item.id))
      );
      toast.success(
        `${selected.length} document${selected.length !== 1 ? "s" : ""} archived. Find them under Archive in the menu.`,
        { duration: TOAST.long }
      );
      setSelectedRows(new Set());
    } catch {
      toast.error("Failed to archive some documents. Please try again.");
    }
  }, [items, selectedRows, archiveMutation]);

  const handleBulkExport = useCallback(() => {
    const selected = items.filter((item) => selectedRows.has(item.id));
    const blob = buildCsv(selected);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = CSV_FILENAME;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(
      `${selected.length} document${selected.length !== 1 ? "s" : ""} exported`
    );
  }, [items, selectedRows]);

  const handleImport = useCallback(
    async (csvText: string) => {
      const result = await importCsvMutation.mutateAsync(csvText);
      const { imported, draft, errors } = result;

      // Show a rich summary toast
      if (draft > 0 && errors.length === 0) {
        toast.success(
          `${imported} document${imported !== 1 ? "s" : ""} imported — ${draft} saved as draft (missing fields)`,
          { duration: 5000 }
        );
      } else if (errors.length > 0) {
        toast(
          `${imported} imported · ${draft} draft · ${errors.length} row${errors.length !== 1 ? "s" : ""} skipped`,
          { icon: "⚠️", duration: 6000 }
        );
      } else {
        toast.success(
          `${imported} document${imported !== 1 ? "s" : ""} imported successfully`
        );
      }
    },
    [importCsvMutation]
  );

  // ── Return ─────────────────────────────────────────────────────────────────

  return {
    items,
    isLoading,
    isModalOpen,
    editingItem,
    modalRenewalMode,
    modalFocusExpiry,
    closeModal,
    drawerItem,
    openDrawer,
    closeDrawer,
    selectedRows,
    handleRowSelect,
    handleClearSelection,
    searchQuery,
    setSearchQuery,
    handleCreate,
    handleEdit,
    handleAddFields,
    handleRenewItem,
    handleSendReminder,
    handleDelete,
    handleSubmit,
    handleRowAction,
    handleBulkRenew,
    handleBulkEditExpiry,
    handleBulkAssignOwner,
    handleBulkChangeCategory,
    handleBulkSnooze,
    handleBulkArchive,
    handleBulkExport,
    handleImport,
  };
}
